/**
 * Typed client for the timeout-recovery flow's support-request submissions
 * (handoff-timeout-panel.tsx's "leave a message" / "request a call-back"
 * views) — `POST /api/conversations/{conversationId}/support-requests`.
 *
 * The two enums (`SupportRequestType`, `CallbackWindow`) are re-exported from
 * the generated contract (same pattern as src/types/conversation.ts) — their
 * string values are verified byte-identical to what used to be hand-written
 * here, so that part of the swap is a pure, zero-risk consolidation. Do NOT
 * hand-edit them again; change `aol_schemas.feedback` (aol-us-shared,
 * feedback and support-requests share one schema module) and regenerate
 * with `./devops build types`.
 *
 * `SupportRequestResponse` is ALSO swapped to the generated DTO — every
 * field's required/optional shape happens to already match how this
 * codebase constructs/consumes it, so the only visible effect is `status`
 * tightening from a plain `string` to the real `SupportRequestStatus` union
 * (`new | pushed | failed`); see that type's doc comment.
 *
 * `SupportRequestPayload` is now the generated `SupportRequestCreate` DTO.
 * A previous attempt left this hand-written because every optional-with-a-
 * default field (handoff_id, body, phone, callback_window) generated as a
 * REQUIRED key typed `T | null`, not an omittable optional key — the
 * opposite of how handoff-timeout-panel.tsx and every test fixture below
 * build this payload (both "leave a message" and "request a callback"
 * submissions each supply only their own subset of fields). That was a
 * codegen flag defect, not a schema decision: the generator now runs with
 * `--default-non-nullable false` for this spec, which emits a
 * Pydantic field-with-a-default as `field?: T | null` instead of
 * required-but-nullable. With that fixed upstream, this is a pure
 * type-identity swap.
 *
 * Follows the same base-URL + X-Device-Token pattern every other REST caller
 * against this backend uses (see useConversations.ts's `conversationFetch`
 * and useVoiceRecording.ts's /api/transcribe call) but is written standalone
 * rather than importing conversationFetch — that helper lives in a
 * hooks-named file for a different (React Query) call site.
 *
 * Non-2xx bodies are normalised via `extractErrorMessage` (./http-error) —
 * shared across every REST caller against this backend, not
 * support-requests-specific; see that module's own doc comment for why a
 * FastAPI 422's `detail` array needs normalising at all (it used to render
 * the literal text "[object Object]").
 */
import { getAssistantApiUrl } from '@/lib/assistant-config';
import { getDeviceToken } from '@/lib/device-token';
import {
  SupportRequestType,
  CallbackWindow,
} from '@/types/generated/feedback-contract';
import type { components } from '@/types/generated/feedback-dto';
import { extractErrorMessage } from './http-error';

export { SupportRequestType, CallbackWindow };

/** Request body for POST .../support-requests. `handoff_id` is optional on
 *  the wire (a support request could in principle outlive its handoff) but
 *  the timeout-recovery flow always has one on hand and always sends it.
 *
 *  Now the generated `SupportRequestCreate` DTO — see this file's header for
 *  why the earlier attempt at this swap was blocked and what fixed it. */
export type SupportRequestPayload =
  components['schemas']['SupportRequestCreate'];

/** 201 response shape. `status` now carries the real generated
 *  `SupportRequestStatus` union (`new | pushed | failed`) instead of the
 *  hand-written plain `string` — the backend's status vocabulary has landed,
 *  so this is a type-safety tightening that falls out of the swap for free.
 *  It's read-only (this module never writes `status`), so narrowing it here
 *  has no behavioral effect on any caller — it only catches wire values that
 *  were never actually possible (see handoff-timeout-panel.test.tsx, which
 *  had to fix two mocked fixtures using a status string the real backend
 *  never sends). */
export type SupportRequestResponse =
  components['schemas']['SupportRequestResponse'];

/**
 * Submit a support request. Throws on any non-2xx response or network
 * failure — callers (handoff-timeout-panel.tsx) are expected to catch this
 * and surface the message inline, never swallow it silently, per the
 * "leave the user's typed content intact" requirement.
 */
export async function submitSupportRequest(
  conversationId: string,
  payload: SupportRequestPayload
): Promise<SupportRequestResponse> {
  const deviceToken = getDeviceToken();
  const response = await fetch(
    getAssistantApiUrl(`/api/conversations/${conversationId}/support-requests`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Token': deviceToken,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => ({}));
    const fallback = `Support request failed: ${response.status} ${response.statusText}`;
    throw new Error(extractErrorMessage(error, fallback));
  }

  return response.json();
}
